import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import {
  FileText,
  Folder,
  Image as ImageIcon,
  Video,
  Files,
  Upload,
  CheckCircle2,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  Select,
  SegmentedControl,
} from "../../design-system";
import { AiModelSelect } from "./llm-model-select";
import type { AiModels } from "./llm-data";
import type { FileRef } from "./library-adapter";
import {
  guidelineFixture,
  refineFixture,
  type Guideline,
  type RefineJob,
} from "./refine-data";
import "./library-upload.css";
export type UploadChannel = "complete" | "refine";
export type RefineBatchInput = {
  customerId: string;
  folderId?: string | null;
  guidelineId?: string;
  model?: string;
  instructions?: string;
  items: {
    path?: string;
    title?: string;
    texts: { name: string; body: string }[];
    files: { fileId: string }[];
  }[];
};
export type RefineBatchResult = {
  items: { path: string; ok: boolean; id?: string; error?: string }[];
};
export type RefineUploadJob = {
  customerId: string;
  folderId: string | null;
  guidelineId?: string;
  model?: string;
  instructions?: string;
  rows: { path: string; title?: string; files: File[] }[];
};
export type RefineUploadProvider = {
  /** Host-resolved HQ file scope; not a manuscript target. */
  hqCustomerId?: string;
  enqueue?: (job: RefineUploadJob) => void;
  guidelines: Guideline[];
  models?: AiModels;
  canCreate: boolean;
  loading?: boolean;
  error?: string;
  readiness?: { reason: string; usedUsd?: number; limitUsd?: number };
  uploadFile?: (customerId: string, file: File) => Promise<FileRef>;
  batch?: (input: RefineBatchInput) => Promise<RefineBatchResult>;
  onOpenList?: (ids: string[]) => void;
  onOpenSettings?: () => void;
};
export type RefineUploadExample =
  | "selected"
  | "empty"
  | "partial"
  | "complete"
  | "uploading"
  | "creating";
export function UploadChannels({
  channel,
  onChange,
  disabled = false,
  completeAvailable = true,
}: {
  channel: UploadChannel;
  onChange: (channel: UploadChannel) => void;
  disabled?: boolean;
  completeAvailable?: boolean;
}) {
  return (
    <fieldset className="mlu-channels" disabled={disabled}>
      <SegmentedControl
        label="올리기 채널"
        value={channel}
        options={[
          { value: "complete", label: "완성 원고로 올리기" },
          { value: "refine", label: "정형화해서 올리기" },
        ]}
        onChange={(v) => {
          if (v !== "complete" || completeAvailable)
            onChange(v as UploadChannel);
        }}
      />
      {!completeAvailable && (
        <small>완성 원고 업로드 연결을 준비하고 있습니다.</small>
      )}
    </fieldset>
  );
}
const textFile = (f: File) => /\.(md|txt|json|csv)$/i.test(f.name);
const imageFile = (f: File) =>
  f.type.startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|heic|avif|bmp|svg)$/i.test(f.name);
const videoFile = (f: File) =>
  f.type.startsWith("video/") ||
  /\.(mp4|mov|m4v|avi|mkv|webm|3gp)$/i.test(f.name);
type Row = {
  path: string;
  files: File[];
  reason?: string;
  result?: RefineBatchResult["items"][number];
};
function groupFiles(
  files: File[],
  mode: string,
  emptyPaths: string[] = [],
): Row[] {
  const groups = new Map<string, File[]>();
  for (const file of files) {
    const path =
      mode === "folder"
        ? file.webkitRelativePath.split("/").slice(0, -1).join("/") ||
          "폴더 밖 원고"
        : "선택한 파일";
    groups.set(path, [...(groups.get(path) ?? []), file]);
  }
  for (const path of emptyPaths) if (!groups.has(path)) groups.set(path, []);
  return [...groups].map(([path, files]) => ({
    path,
    files,
    reason: files.some((f) => /\.(md|txt)$/i.test(f.name))
      ? undefined
      : "텍스트 파일 없음(.md/.txt) · 건너뜀",
  }));
}
function sampleFiles() {
  const file = (path: string, body = "자료") => {
    const f = new File([body], path.split("/").at(-1)!);
    Object.defineProperty(f, "webkitRelativePath", { value: path });
    return f;
  };
  return [
    file("10월/예약/원고.md", "# 예약 안내\n\n방문 전에 확인해 주세요."),
    file("10월/예약/보조.txt"),
    file("10월/예약/등록정보.json", '{"seoKeywords":["진료 예약"]}'),
    file("10월/예약/clinic.jpg"),
    file("10월/예약/guide.pdf"),
    file("10월/진료/안내.md", "# 진료 안내"),
    file("10월/진료/참고.csv", "항목,내용"),
    file("10월/진료/intro.mp4"),
    file("10월/상담/원고.txt", "# 상담 안내"),
    file("10월/사진만/room.jpg"),
  ];
}
/** Presentation-only fixtures. Real hosts supply uploadFile/batch and own navigation. */
export function createRefineUploadFixture(
  onCreated?: (jobs: RefineJob[]) => void,
): RefineUploadProvider {
  let sequence = 100;
  const names = new Map<string, string>();
  return {
    hqCustomerId: "hq",
    guidelines: guidelineFixture,
    models: { default: "gpt-5-mini", allowed: ["gpt-5-mini"] },
    canCreate: true,
    uploadFile: async (_, file) => {
      await new Promise((r) => setTimeout(r, 60));
      const ref = { fileId: `demo-file-${++sequence}`, name: file.name };
      names.set(ref.fileId, file.name);
      return ref;
    },
    batch: async (input) => {
      await new Promise((r) => setTimeout(r, 250));
      const jobs = input.items
        .filter((i) => i.texts.length)
        .map((i) => {
          const j = refineFixture("queued");
          j.id = `REF-${++sequence}`;
          j.customerId = input.customerId;
          j.folderId = input.folderId ?? "";
          j.title = i.title ?? i.path ?? "올린 원고";
          j.sources = [
            {
              revision: 1,
              body: i.texts[0].body,
              files: i.files.map((f) => ({
                fileId: f.fileId,
                name: names.get(f.fileId) ?? f.fileId,
              })),
            },
          ];
          j.attempts = [
            { ...j.attempts.at(-1)!, seq: 1, sourceRevision: 1, body: "" },
          ];
          return j;
        });
      onCreated?.(jobs);
      let n = 0;
      return {
        items: input.items.map((i) =>
          i.texts.length
            ? { path: i.path ?? "", ok: true, id: jobs[n++].id }
            : {
                path: i.path ?? "",
                ok: false,
                error: "no_text: 텍스트 파일 없음",
              },
        ),
      };
    },
  };
}
export function RefineBatchUpload({
  customerId,
  onClose,
  channelHeader,
  refine,
  initialFiles,
  example,
  onSelection,
}: {
  onTargetChange?: (target: {
    customerId: string;
    folderId: string | null;
  }) => void;
  targetLabel?: string;
  onSelection?: (files: File[]) => void;
  customerId: string;
  folderId: string | null;
  onClose: () => void;
  channelHeader: (busy: boolean) => ReactNode;
  refine: RefineUploadProvider;
  initialFiles?: File[];
  example?: RefineUploadExample;
  targets?: {
    customers: { id: string; name: string }[];
    folders: { id: string; customerId: string; name: string }[];
  };
}) {
  const customer = refine.hqCustomerId ?? customerId;
  const folder = "";
  const [mode, setMode] = useState(
    (example && example !== "empty") ||
      initialFiles?.some((f) => f.webkitRelativePath.includes("/"))
      ? "folder"
      : "file",
  );
  const [rows, setRows] = useState<Row[]>(() => {
    const rows = initialFiles?.length
      ? groupFiles(
          initialFiles,
          initialFiles.some((f) => f.webkitRelativePath.includes("/"))
            ? "folder"
            : "file",
        )
      : example && example !== "empty"
        ? groupFiles(sampleFiles(), "folder")
        : [];
    return rows.map((r, i) => ({
      ...r,
      result:
        ["partial", "complete"].includes(example ?? "") && !r.reason
          ? {
              path: r.path,
              ok: example === "complete" || i !== 1,
              id:
                i === 1 && example === "partial" ? undefined : `REF-${100 + i}`,
              error:
                i === 1 && example === "partial"
                  ? "파일을 읽을 수 없습니다. 다시 올려 주세요."
                  : undefined,
            }
          : undefined,
    }));
  });
  const [phase, setPhase] = useState<
    "ready" | "uploading" | "creating" | "complete"
  >(
    example === "uploading" || example === "creating"
      ? example
      : example === "partial" || example === "complete"
        ? "complete"
        : "ready",
  );
  const [progress, setProgress] = useState({
      uploaded: example === "creating" ? 3 : example === "uploading" ? 1 : 0,
      files: 3,
      created: 0,
      jobs: 3,
    }),
    [instructions, setInstructions] = useState(""),
    [guideline, setGuideline] = useState(
      () =>
        refine.guidelines.find((g) => g.kind === "refine" && g.active)?.id ??
        "",
    ),
    [model, setModel] = useState(""),
    [error, setError] = useState("");
  const pending = useRef(false),
    live = useRef(true),
    cache = useRef(new Map<File, FileRef>());
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const busy = phase === "uploading" || phase === "creating",
    ids = rows.flatMap((r) =>
      r.result?.ok && r.result.id ? [r.result.id] : [],
    ),
    skipped = rows.filter((r) => r.reason).length,
    failed = rows.filter((r) => r.result && !r.result.ok).length;
  const canStart =
    refine.canCreate &&
    !refine.loading &&
    !refine.error &&
    !refine.readiness &&
    (!!refine.enqueue || (!!refine.batch && !!refine.uploadFile)) &&
    !!customer;
  function choose(files: File[], nextMode = mode, emptyPaths: string[] = []) {
    if (busy || pending.current) return;
    const next = groupFiles(files, nextMode, emptyPaths);
    onSelection?.(files);
    setRows(next);
    setPhase("ready");
    setError(
      next.length > 50 ? "한 번에 최대 50개 폴더를 올릴 수 있습니다." : "",
    );
    cache.current.clear();
  }
  async function start() {
    if (pending.current || busy || !canStart) return;
    const pendingRows = rows.filter((r) => !r.reason && !r.result?.ok);
    if (!pendingRows.length || rows.length > 50) return;
    pending.current = true;
    setError("");
    if (refine.enqueue) {
      try {
        refine.enqueue({
          customerId: customer,
          folderId: folder || null,
          guidelineId: guideline || undefined,
          model: model || undefined,
          instructions: instructions || undefined,
          rows: rows.map((r) => ({
            path: r.path,
            title: mode === "folder" ? r.path.split("/").at(-1) : undefined,
            files: [...r.files],
          })),
        });
        onClose();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        pending.current = false;
      }
      return;
    }
    setPhase("uploading");
    const stats = {
      uploaded: 0,
      files: pendingRows.flatMap((r) => r.files.filter((f) => !textFile(f)))
        .length,
      created: 0,
      jobs: pendingRows.length,
    };
    setProgress({ ...stats });
    const next = rows.map((r) => ({ ...r }));
    const items: RefineBatchInput["items"] = [];
    try {
      for (const row of pendingRows) {
        const target = next.find((r) => r.path === row.path)!;
        target.result = undefined;
        try {
          const texts = [];
          const files: FileRef[] = [];
          for (const file of row.files) {
            if (textFile(file)) {
              texts.push({ name: file.name, body: await file.text() });
            } else {
              let ref = cache.current.get(file);
              if (!ref) {
                ref = await refine.uploadFile!(customer, file);
                cache.current.set(file, ref);
              }
              files.push(ref);
              stats.uploaded++;
              if (live.current) setProgress({ ...stats });
            }
          }
          items.push({
            path: row.path,
            title: mode === "folder" ? row.path.split("/").at(-1) : undefined,
            texts,
            files: files.map((f) => ({ fileId: f.fileId })),
          });
        } catch (e) {
          target.result = {
            path: row.path,
            ok: false,
            error: e instanceof Error ? e.message : String(e),
          };
        }
        if (!live.current) return;
        setRows([...next]);
      }
      if (items.length) {
        setPhase("creating");
        stats.jobs = items.length;
        setProgress({ ...stats });
        const result = await refine.batch!({
          customerId: customer,
          folderId: folder || null,
          guidelineId: guideline || undefined,
          model: model || undefined,
          instructions: instructions || undefined,
          items,
        });
        for (const item of items) {
          const row = next.find((r) => r.path === item.path)!;
          row.result = result.items.find((r) => r.path === item.path) ?? {
            path: item.path ?? "",
            ok: false,
            error: "처리 결과가 없습니다. 다시 확인해 주세요.",
          };
        }
        for (const row of next)
          if (row.result?.ok && !row.result.id)
            row.result = {
              path: row.path,
              ok: false,
              error: "작업 ID를 받지 못했습니다. 처리 결과를 확인해 주세요.",
            };
        stats.created = result.items.length;
        if (live.current) setProgress({ ...stats });
      }
      if (live.current) {
        setRows(next);
        setPhase("complete");
      }
    } catch (e) {
      if (live.current) {
        setError(e instanceof Error ? e.message : String(e));
        setRows(next);
        setPhase("ready");
      }
    } finally {
      pending.current = false;
    }
  }
  async function drop(event: React.DragEvent) {
    event.preventDefault();
    if (busy) return;
    const entries = Array.from(event.dataTransfer.items)
      .map((i) => i.webkitGetAsEntry())
      .filter((e): e is FileSystemEntry => !!e);
    if (!entries.some((e) => e.isDirectory)) {
      choose(Array.from(event.dataTransfer.files));
      return;
    }
    const empty: string[] = [];
    async function walk(e: FileSystemEntry, prefix = ""): Promise<File[]> {
      const path = prefix + e.name;
      if (e.isFile)
        return new Promise((resolve, reject) =>
          (e as FileSystemFileEntry).file((f) => {
            Object.defineProperty(f, "webkitRelativePath", { value: path });
            resolve([f]);
          }, reject),
        );
      const reader = (e as FileSystemDirectoryEntry).createReader();
      const children: FileSystemEntry[] = [];
      while (true) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
          reader.readEntries(resolve, reject),
        );
        if (!batch.length) break;
        children.push(...batch);
      }
      if (!children.length) empty.push(path);
      return (
        await Promise.all(children.map((c) => walk(c, path + "/")))
      ).flat();
    }
    try {
      const files = (await Promise.all(entries.map((e) => walk(e)))).flat();
      setMode("folder");
      choose(files, "folder", empty);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <Dialog
      open
      title="원고 올리기"
      description="파일을 함께 올려 정형화 작업을 만듭니다. 결과와 검토는 정형화 목록에서 확인하세요."
      size="wide"
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
      footer={
        <DialogActions
          secondary={{
            label: phase === "complete" ? "닫기" : "취소",
            disabled: busy,
            onClick: onClose,
          }}
          primary={
            ids.length
              ? {
                  label: "정형화 목록 보기",
                  disabled: busy || !refine.onOpenList,
                  onClick: () => {
                    refine.onOpenList?.(ids);
                    onClose();
                  },
                }
              : {
                  label: busy
                    ? phase === "uploading"
                      ? "업로드 중…"
                      : "작업 만드는 중…"
                    : "정형화 시작",
                  disabled:
                    busy ||
                    !canStart ||
                    rows.length > 50 ||
                    !rows.some((r) => !r.reason && !r.result?.ok),
                  onClick: () => void start(),
                }
          }
        />
      }
    >
      <div className="ml-upload mlu-refine" data-refine-upload>
        {channelHeader(busy)}
        {refine.enqueue && (
          <p className="mlu-help">
            시작하면 창이 닫히고 업로드 트레이에서 계속됩니다. 다른 메뉴로
            이동해도 업로드는 이어집니다.
          </p>
        )}
        <p className="mlu-help">
          메타크루 소유 원고로 정형화합니다. 대상 폴더는 등록할 때 선택합니다.
        </p>
        <fieldset className="mlu-channels" disabled={busy}>
          <SegmentedControl
            label="올리기 방법"
            value={mode}
            options={[
              { value: "file", label: "파일 올리기" },
              { value: "folder", label: "폴더 올리기" },
            ]}
            onChange={(v) => {
              setMode(v);
              choose([], v);
            }}
          />
        </fieldset>
        <div
          className="ml-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => void drop(e)}
        >
          <Upload size={28} />
          <b>폴더를 통째로 올리세요</b>
          <p>원고·보조 문서·사진·영상·그 밖 파일 모두</p>
          <small>
            {mode === "folder"
              ? "폴더 하나 = 정형화 작업 1건 · 최대 50건"
              : "고른 파일 전부 = 정형화 작업 1건"}{" "}
            · 파일 종류 제한 없음
          </small>
          <input
            key={mode}
            type="file"
            multiple
            disabled={busy}
            aria-label={
              mode === "folder" ? "업로드 폴더 선택" : "업로드 파일 선택"
            }
            {...(mode === "folder"
              ? ({
                  webkitdirectory: "",
                  directory: "",
                } as InputHTMLAttributes<HTMLInputElement>)
              : {})}
            onChange={(e) => choose(Array.from(e.target.files ?? []))}
          />
        </div>
        {!rows.length && (
          <p className="mlu-empty">
            아직 선택한 파일이 없습니다. 원고와 자료가 든 폴더를 선택해 주세요.
          </p>
        )}
        <details className="mlu-options">
          <summary>
            정형화 설정 <span>활성 가이드라인 · 기본 모델</span>
          </summary>
          <fieldset disabled={busy}>
            <label className="mlu-field">
              지시사항 (선택)
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="원문을 유지하며 정리해 주세요."
              />
            </label>
            <Select
              label="가이드라인 버전"
              value={guideline}
              onChange={(e) => setGuideline(e.target.value)}
            >
              <option value="">활성 버전 사용</option>
              {refine.guidelines
                .filter((g) => g.kind === "refine")
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.active ? "활성 · " : ""}v{g.version} {g.label}
                  </option>
                ))}
            </Select>
            <AiModelSelect
              models={refine.models}
              value={model}
              onChange={setModel}
              disabled={busy || refine.loading}
              alwaysShow
            />
          </fieldset>
        </details>
        {refine.readiness && (
          <div className="mlu-warning" role="alert">
            {refine.readiness.reason}
            {refine.readiness.usedUsd !== undefined && (
              <p>
                오늘 ${refine.readiness.usedUsd} / 예산 $
                {refine.readiness.limitUsd}
              </p>
            )}
            {refine.onOpenSettings && (
              <Button
                variant="secondary"
                size="sm"
                onClick={refine.onOpenSettings}
              >
                설정 보기
              </Button>
            )}
          </div>
        )}
        {(error || refine.error) && (
          <p className="ml-error" role="alert">
            {error || refine.error}
          </p>
        )}
        {refine.loading && <p role="status">정형화 설정을 읽고 있습니다…</p>}
        {!refine.canCreate && (
          <p className="mlu-warning">정형화 작업을 만들 권한이 없습니다.</p>
        )}
        {busy && (
          <div className="mlu-progress" role="status" aria-live="polite">
            <strong>
              {phase === "uploading"
                ? `업로드 ${progress.uploaded}/${progress.files}`
                : `만들기 ${progress.created}/${progress.jobs}`}
            </strong>
            <progress
              aria-label={
                phase === "uploading" ? "파일 업로드 진행" : "작업 만들기 진행"
              }
              value={
                phase === "uploading" ? progress.uploaded : progress.created
              }
              max={Math.max(
                1,
                phase === "uploading" ? progress.files : progress.jobs,
              )}
            />
            <small>
              업로드 {progress.uploaded}/{progress.files} · 만들기{" "}
              {progress.created}/{progress.jobs}
            </small>
          </div>
        )}
        {phase === "complete" && (
          <div className="mlu-summary" role="status">
            <CheckCircle2 size={20} />
            <div>
              <strong>
                작업 {ids.length}건 만듦
                {skipped > 0 && ` · ${skipped}건 건너뜀(텍스트 없음)`}
                {failed > 0 && ` · ${failed}건 실패`}
              </strong>
              <p>생성된 작업은 정형화 목록에서 이어서 확인하세요.</p>
            </div>
            {failed > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={!canStart}
                onClick={() => void start()}
              >
                실패 항목 다시 시도
              </Button>
            )}
          </div>
        )}
        <div className="mlu-rows">
          {rows.map((row) => {
            const counts = {
              text: row.files.filter(textFile).length,
              image: row.files.filter(imageFile).length,
              video: row.files.filter(videoFile).length,
            };
            const other = row.files.filter(
              (f) => !textFile(f) && !imageFile(f) && !videoFile(f),
            );
            const extensions = other.reduce(
              (map, f) => {
                const ext = f.name.includes(".")
                  ? f.name.split(".").at(-1)!.toLowerCase()
                  : "확장자 없음";
                map[ext] = (map[ext] ?? 0) + 1;
                return map;
              },
              {} as Record<string, number>,
            );
            return (
              <article
                key={row.path}
                className={row.reason ? "is-skipped" : ""}
              >
                <header>
                  <Folder size={18} />
                  <strong>{row.path}</strong>
                  <Badge
                    tone={
                      row.reason
                        ? "neutral"
                        : row.result?.ok
                          ? "teal"
                          : row.result
                            ? "coral"
                            : "blue"
                    }
                  >
                    {row.reason
                      ? "건너뜀"
                      : row.result?.ok
                        ? "작업 생성됨"
                        : row.result
                          ? "실패"
                          : "정형화 대기"}
                  </Badge>
                </header>
                <div className="mlu-counts">
                  <span>
                    <FileText size={14} />
                    텍스트 {counts.text}
                  </span>
                  <span>
                    <ImageIcon size={14} />
                    사진 {counts.image}
                  </span>
                  <span>
                    <Video size={14} />
                    영상 {counts.video}
                  </span>
                  <span>
                    <Files size={14} />
                    기타 {other.length}
                  </span>
                  {Object.entries(extensions).map(([ext, n]) => (
                    <Badge key={ext}>
                      {ext} {n}
                    </Badge>
                  ))}
                </div>
                {(row.reason || row.result?.error) && (
                  <p
                    role={row.result?.error ? "alert" : undefined}
                    className={row.result?.error ? "ml-error" : ""}
                  >
                    {row.reason ?? row.result?.error}
                  </p>
                )}
              </article>
            );
          })}
        </div>
        <p className="ml-caption">
          형식 검사는 정형화가 끝난 뒤 진행합니다. 지원하지 않는 파일과 보조
          문서도 결과 보고서에 남습니다.
        </p>
      </div>
    </Dialog>
  );
}
